import { useAccommodationDetailSuspenseQuery } from '@/api/accommodation/hooks'
import { type Image } from '@/api/accommodation/types'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { BackLink } from '@/components/BackLink'
import { unitFullName, unitPhotos } from '@/lib/unit'

interface GalleryContentProps {
  slug: string
  unitSlug: string
}

export function GalleryContent({ slug, unitSlug }: GalleryContentProps) {
  const { data } = useAccommodationDetailSuspenseQuery(slug, unitSlug)
  const photos = unitPhotos(data.unit, data)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)

  // Sort images by priority once
  const sortedImages = [...photos].sort((a, b) => b.priority - a.priority)

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (selectedImageIndex === null) return

      switch (e.key) {
        case 'Escape':
          setSelectedImageIndex(null)
          break
        case 'ArrowLeft':
          e.preventDefault()
          setSelectedImageIndex((prev) => (prev === null ? null : prev > 0 ? prev - 1 : sortedImages.length - 1))
          break
        case 'ArrowRight':
          e.preventDefault()
          setSelectedImageIndex((prev) => (prev === null ? null : prev < sortedImages.length - 1 ? prev + 1 : 0))
          break
      }
    }

    if (selectedImageIndex !== null) {
      window.addEventListener('keydown', handleKeyPress)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
      document.body.style.overflow = 'unset'
    }
  }, [selectedImageIndex, sortedImages.length])

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index)
  }

  const closeLightbox = () => {
    setSelectedImageIndex(null)
  }

  const goToPrevious = () => {
    setSelectedImageIndex((prev) => (prev === null ? null : prev > 0 ? prev - 1 : sortedImages.length - 1))
  }

  const goToNext = () => {
    setSelectedImageIndex((prev) => (prev === null ? null : prev < sortedImages.length - 1 ? prev + 1 : 0))
  }

  const selectedImage = selectedImageIndex !== null ? sortedImages[selectedImageIndex] : undefined

  const backButton = <BackLink>Zpět na detail ubytování</BackLink>

  if (photos.length === 0) {
    return (
      <div className="container py-8">
        {backButton}
        <div className="py-12 text-center">
          <p className="text-slate-500">Pro toto ubytování nejsou k dispozici žádné fotografie.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container py-8">
      <div className="mb-8">
        {backButton}

        <h1 className="mb-2 text-3xl font-bold">{unitFullName(data, data.unit)}</h1>
        <p className="text-slate-600">Galerie fotografií ({sortedImages.length} fotek)</p>
      </div>

      {/* Masonry layout */}
      <div className="columns-1 gap-4 space-y-4 md:columns-2 lg:columns-3 xl:columns-4">
        {sortedImages.map((image: Image, index) => (
          <div key={index} className="mb-4 break-inside-avoid">
            <button
              type="button"
              className="block w-full cursor-pointer"
              aria-label={`Zvětšit fotografii ${index + 1}`}
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.url.thumbnail}
                alt={image.description}
                className="w-full rounded-lg object-cover transition-opacity hover:opacity-90"
                loading={index < 8 ? 'eager' : 'lazy'}
              />
            </button>
            {image.description && <p className="mt-2 px-1 text-sm text-slate-600">{image.description}</p>}
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && selectedImageIndex !== null && (
        // The backdrop click is a mouse shortcut; keyboard users close the
        // lightbox with Escape (handled by the window keydown listener).
        <div
          role="presentation"
          className="bg-opacity-90 fixed inset-0 z-50 flex items-center justify-center bg-black"
          onClick={(e) => {
            // Clicking the photo itself keeps the lightbox open
            if (e.target instanceof HTMLImageElement) return
            closeLightbox()
          }}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]">
            {/* Close button */}
            <Button
              variant="secondary"
              size="icon"
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 rounded-full bg-black/50 text-white hover:bg-black/70"
            >
              <X className="h-4 w-4" />
            </Button>

            {/* Previous button */}
            {sortedImages.length > 1 && (
              <Button
                variant="secondary"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevious()
                }}
                className="absolute top-1/2 left-4 z-10 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            {/* Next button */}
            {sortedImages.length > 1 && (
              <Button
                variant="secondary"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation()
                  goToNext()
                }}
                className="absolute top-1/2 right-4 z-10 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {/* Image */}
            <img
              src={selectedImage.url.original}
              alt={selectedImage.description}
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />

            {/* Image info */}
            {selectedImage.description && (
              <div className="absolute right-4 bottom-4 left-4 rounded-lg bg-black/70 p-4 text-white">
                <p className="text-sm">{selectedImage.description}</p>
                <p className="mt-1 text-xs text-slate-300">
                  {selectedImageIndex + 1} z {sortedImages.length}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
